import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../lib/api";
import { ConfirmDialog } from "../common/ConfirmDialog";
import { ToolModal } from "../agent/ConfigTools";
import { VersionBadge, globalTag } from "../agent/SelfService";
import { DialogPanel } from "../shared/DialogPanel";
import { backdropClose } from "../shared/backdrop";

// Modelos de tool vivem na tabela dedicada /tool-templates. Reusa o editor de tools
// (ToolModal em templateMode) intacto — com cooldown, override de modelo, prompts etc.
export function ToolTemplatesTab({ readOnly = false }: { readOnly?: boolean } = {}) {
  const qc = useQueryClient();
  const [picker, setPicker] = useState(false);
  const [creating, setCreating] = useState<{ kind: "http" | "python"; global?: boolean } | null>(null);
  const [editing, setEditing] = useState<any | null>(null);
  const [del, setDel] = useState<any | null>(null);

  const { data: allTemplates = [], isLoading } = useQuery<any[]>({
    queryKey: ["tool-templates"],
    queryFn: () => api.get("/tool-templates"),
  });
  // O template da Tool de Estoque tem aba própria ("Tool de Estoque") — fora desta lista.
  const templates = allTemplates.filter((t: any) => t.kind !== "estoque");
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/tool-templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tool-templates"] }),
  });

  if (isLoading) return <div style={{ color: "var(--ink-muted)", fontSize: 13 }}>Carregando…</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <span style={{ fontSize: 13, color: "var(--ink-secondary)" }}>
          {templates.length} modelo{templates.length !== 1 ? "s" : ""} de tool
          {readOnly && <span style={{ marginLeft: 8, fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "var(--surface-hover)", color: "var(--ink-muted)", border: "1px solid var(--line)" }}>somente leitura</span>}
        </span>
        {!readOnly && <button
          onClick={() => setPicker(true)}
          style={{
            background: "var(--brand)",
            color: "#fff",
            border: "none",
            borderRadius: 8,
            padding: "7px 14px",
            fontSize: 13,
            cursor: "pointer",
          }}
        >
          + Novo modelo
        </button>}
      </div>

      {templates.length === 0 ? (
        <div
          style={{
            padding: 32,
            textAlign: "center",
            color: "var(--line-max)",
            fontSize: 13,
            border: "1px dashed var(--line)",
            borderRadius: 8,
          }}
        >
          Nenhum modelo de tool cadastrado ainda.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {templates.map((tpl: any) => (
            <div
              key={tpl.id}
              onClick={() => setEditing(tpl)}
              style={{
                background: "var(--surface-hover)",
                border: "1px solid var(--line)",
                borderRadius: 8,
                padding: "12px 16px",
                cursor: "pointer",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 8,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: 13, color: "var(--ink)", display: "flex", alignItems: "center", gap: 8 }}>{tpl.name}{Array.isArray(tpl.editable_fields) && <span style={globalTag}>Global</span>}<VersionBadge versions={tpl.versions} /></div>
                {(tpl.template_description || tpl.description) && (
                  <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 4 }}>{tpl.template_description || tpl.description}</div>
                )}
                <div style={{ fontSize: 11, color: "var(--line-max)", marginTop: 6, fontFamily: "var(--font-mono)" }}>
                  {tpl.kind === "python" ? "Python" : `${tpl.method ?? ""} ${tpl.url ?? ""}`}
                </div>
              </div>
              {!readOnly && <button
                title="Excluir modelo"
                disabled={deleteMut.isPending}
                onClick={(e) => { e.stopPropagation(); setDel(tpl); }}
                style={{ background: "none", border: "none", color: "var(--ink-muted)", fontSize: 15, cursor: "pointer", lineHeight: 1, padding: 4, flexShrink: 0 }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "var(--brand)")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "var(--ink-muted)")}
              >
                🗑
              </button>}
            </div>
          ))}
        </div>
      )}

      {picker && (
        <div
          {...backdropClose(() => { setPicker(false); })}
          style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: "var(--z-modal)" as any, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <DialogPanel onClose={() => setPicker(false)} label="Novo modelo de tool" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, display: "flex", flexDirection: "column", gap: 12, width: 520 }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>Novo modelo de tool</span>
            <div style={{ display: "flex", gap: 10 }}>
              {(["http", "python", "global-http", "global-python"] as const).map((opt) => (
                <button
                  key={opt}
                  onClick={() => { setPicker(false); setCreating({ kind: opt.endsWith("python") ? "python" : "http", global: opt.startsWith("global") }); }}
                  style={{ flex: 1, background: "var(--surface-input)", color: "var(--ink)", border: "1px solid var(--line-strong)", borderRadius: 8, padding: "16px 0", fontSize: 13, cursor: "pointer", transition: "border-color .12s, background .12s" }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = "var(--brand)"; e.currentTarget.style.background = "var(--brand-soft)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = "var(--line-strong)"; e.currentTarget.style.background = "var(--surface-input)"; }}
                >
                  {opt === "http" ? "HTTP" : opt === "python" ? "Python" : opt === "global-http" ? "Global HTTP" : "Global Python"}
                </button>
              ))}
            </div>
            <span style={{ fontSize: 11, color: "var(--ink-soft)" }}>Global: tool regida pelo Dev, vinculada ao vivo nos agentes; você marca quais campos o gestor edita.</span>
          </DialogPanel>
        </div>
      )}

      {creating && (
        <ToolModal
          initialKind={creating.kind}
          initialStep="form"
          onClose={() => setCreating(null)}
          templateMode
          initialGlobal={!!creating.global}
        />
      )}
      {editing && (
        <ToolModal key={editing.id} tool={editing} onClose={() => setEditing(null)} templateMode />
      )}
      {del && (
        <ConfirmDialog
          title={`Excluir "${del.name}"?`}
          confirmLabel="Excluir"
          danger
          onConfirm={() => { deleteMut.mutate(del.id); setDel(null); }}
          onCancel={() => setDel(null)}
        />
      )}
    </div>
  );
}
